import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon } from '../../../icons';
import { Signature } from '../../../api/extDocumentMeta';

/**
 * Simple component to render stage icon
 */
const StageIcon: React.FC<{
  signature: Signature;
}> = ({ signature }) => {
  const theme = useMantineTheme();

  if (signature.done && signature.date) {
    return <CheckCircleIcon size={35} color="green" />;
  }

  // processing
  return (
    <Box
      w={33}
      h={33}
      sx={{
        border: `2px solid ${theme.colors.green[0]}`,
        borderStyle: 'dashed',
        borderRadius: '100%',
      }}
    />
  );
};

export type SignatureQueueProps = {
  /** Signature queue */
  queue: Signature[];
};

/**
 * Component to render signature queue
 *
 * @param queue - array of signatures
 */
export const SignatureQueue: React.FC<SignatureQueueProps> = ({ queue }) => {
  const { t } = useTranslation(['documentSignatures', 'common']);
  const theme = useMantineTheme();

  /**
   * Get category label based on the signature category
   * @param category - signature category
   * @param done - whether the signature is done
   */
  const getCategoryLabel = ({ category, done }: Partial<Signature>) => {
    if (!category || !done) {
      return '';
    }

    switch (category) {
      case 'DOCUMENT':
        return t('documentSignatures:signature.document');
      case 'QUALIFIED':
        return t('documentSignatures:signature.qualified');
      default:
        return '';
    }
  };

  return (
    <Stack spacing={4}>
      {queue.map((signature, index) => (
        <Box key={`${signature.user.email}`} sx={{ display: 'flex', gap: 16 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                flex: '1 1 35px',
              }}
            >
              <StageIcon signature={signature} />
            </Box>
            {index !== queue.length - 1 && (
              <Box
                bg={theme.colors.green[0]}
                sx={{
                  width: '2px',
                  height: '100%',
                }}
              />
            )}
          </Box>
          <Stack spacing={0} pt={8} pb={16} sx={{ overflow: 'hidden' }}>
            <Box>
              <Text
                fz="xl"
                fw={600}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {signature.user.isDeleted
                  ? t('common:accountDeleted')
                  : signature.user.email}
              </Text>
            </Box>

            <Text fz="md" fw={500} color="gray.7">
              {signature.date && new Date(signature.date).toLocaleDateString()}
            </Text>
            <Text fz="md" fw={500} color="gray.7">
              {getCategoryLabel(signature)}
            </Text>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};
